'use client';

import { createClient } from '@/lib/supabase/client';

export interface MentionUser {
  id: string;
  username: string | null;
  fullName: string;
  avatarUrl: string | null;
  tier: string;
}

export interface MentionNotification {
  id: string;
  recipientId: string;
  actorId: string | null;
  actorName: string | null;
  actorAvatar: string | null;
  contentType: 'comment' | 'forum_post' | 'forum_reply';
  contentId: string;
  contentSnippet: string | null;
  readAt: string | null;
  createdAt: string;
}

/** Search users for @mention autocomplete */
export async function searchUsersForMention(query: string): Promise<MentionUser[]> {
  if (!query || query.length < 1) return [];
  const supabase = createClient();
  const { data, error } = await supabase.rpc('search_users_for_mention', {
    search_query: query,
    result_limit: 8,
  });
  if (error) {
    console.log('searchUsersForMention error:', error.message);
    return [];
  }
  return (data || []).map((row: any) => ({
    id: row.id,
    username: row.username,
    fullName: row.full_name,
    avatarUrl: row.avatar_url,
    tier: row.tier,
  }));
}

/** Extract @mentioned usernames from text */
export function extractMentions(text: string): string[] {
  const matches = text.match(/@([\w.-]+)/g) || [];
  return [...new Set(matches.map((m) => m.slice(1).toLowerCase()))];
}

/** Resolve usernames to user IDs */
export async function resolveUsernames(usernames: string[]): Promise<{ id: string; username: string }[]> {
  if (!usernames.length) return [];
  const supabase = createClient();
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, username')
    .in('username', usernames);
  if (error) {
    console.log('resolveUsernames error:', error.message);
    return [];
  }
  return (data || []).map((row: any) => ({ id: row.id, username: row.username }));
}

/** Create mention notifications for all mentioned users */
export async function createMentionNotifications({
  actorId,
  contentType,
  contentId,
  contentSnippet,
  mentionedUserIds,
}: {
  actorId: string;
  contentType: 'comment' | 'forum_post' | 'forum_reply';
  contentId: string;
  contentSnippet: string;
  mentionedUserIds: string[];
}): Promise<void> {
  if (!mentionedUserIds.length) return;
  const supabase = createClient();
  // Don't notify yourself
  const recipients = mentionedUserIds.filter((id) => id !== actorId);
  if (!recipients.length) return;

  const rows = recipients.map((recipientId) => ({
    recipient_id: recipientId,
    actor_id: actorId,
    content_type: contentType,
    content_id: contentId,
    content_snippet: contentSnippet.slice(0, 200),
  }));

  const { error } = await supabase.from('mention_notifications').insert(rows);
  if (error) console.log('createMentionNotifications error:', error.message);
}

/** Fetch mention notifications for the current user */
export async function fetchMentionNotifications(userId: string): Promise<MentionNotification[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('mention_notifications')
    .select(`
      id,
      recipient_id,
      actor_id,
      content_type,
      content_id,
      content_snippet,
      read_at,
      created_at,
      actor:user_profiles!mention_notifications_actor_id_fkey(full_name, avatar_url)
    `)
    .eq('recipient_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) {
    console.log('fetchMentionNotifications error:', error.message);
    return [];
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    recipientId: row.recipient_id,
    actorId: row.actor_id,
    actorName: row.actor?.full_name || null,
    actorAvatar: row.actor?.avatar_url || null,
    contentType: row.content_type,
    contentId: row.content_id,
    contentSnippet: row.content_snippet,
    readAt: row.read_at,
    createdAt: row.created_at,
  }));
}

/** Mark a mention notification as read */
export async function markMentionRead(notificationId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('mention_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', notificationId);
  if (error) console.log('markMentionRead error:', error.message);
}

/** Mark all mention notifications as read for a user */
export async function markAllMentionsRead(userId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('mention_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('recipient_id', userId)
    .is('read_at', null);
  if (error) console.log('markAllMentionsRead error:', error.message);
}
